<template>
  <div class="products">
    <mainmenu selected="products"></mainmenu>

    <div class="admin-page-shell">
      <div class="admin-toolbar">
        <div>
          <h1>{{ $t("products.page_title") }}</h1>
          <div class="toolbar-subtitle">
            {{ $t("products.total_products") }} <strong>{{ PaginationInfo.RowCount }}</strong>
          </div>
        </div>
        <el-button type="primary" icon="el-icon-plus" @click="add_item()">{{ $t("add_item") }}</el-button>
      </div>

      <div class="admin-filters products-filters">
        <div class="filters-title">{{ $t("products.filters") }}</div>
        <el-autocomplete
          class="filter-control filter-search"
          v-model="Filters.ArticleCode"
          :fetch-suggestions="queryCodes"
          :placeholder="$t('products.search_placeholder')"
          prefix-icon="el-icon-search"
          clearable
          @input="on_filter_input"
          @select="apply_filters"
          @clear="apply_filters">
        </el-autocomplete>

        <el-select
          class="filter-control"
          v-model="Filters.IdCategory"
          :placeholder="$t('products.category')"
          filterable
          @change="apply_filters">
          <el-option :label="$t('all')" :value="-1" :key="-1"></el-option>
          <el-option v-for="c in Categories" :label="c.Name" :value="c.Id" :key="c.Id"></el-option>
        </el-select>

        <el-select
          class="filter-control"
          v-model="Filters.ApprovalStatus"
          :placeholder="$t('products.approval_status')"
          clearable
          @change="apply_filters">
          <el-option :label="$t('products.approved')" value="approved"></el-option>
          <el-option :label="$t('products.pending')" value="pending"></el-option>
        </el-select>

        <el-select
          class="filter-control"
          v-model="Filters.CatalogStatus"
          :placeholder="$t('products.catalog_status')"
          clearable
          @change="apply_filters">
          <el-option :label="$t('products.manual_or_legacy')" value="__manual__"></el-option>
          <el-option v-for="s in CatalogStatuses" :label="s.CatalogStatus" :value="s.CatalogStatus" :key="s.CatalogStatus"></el-option>
        </el-select>

        <el-button type="primary" icon="el-icon-search" @click="apply_filters">{{ $t("filter") }}</el-button>
        <el-button class="reset-filters" icon="el-icon-refresh-left" @click="reset_filters">{{ $t("reset") }}</el-button>
      </div>

      <div class="admin-card">
        <div class="admin-card-heading">
          <div>
            <h2>{{ $t("products.list_title") }}</h2>
            <span>{{ $t("products.sorted_by_newest") }}</span>
          </div>
          <el-tag size="small" type="info">{{ $t("products.total") }} {{ PaginationInfo.RowCount }}</el-tag>
        </div>

        <el-table
          class="admin-table"
          :data="Results"
          style="width: 100%"
          :max-height="TableMaxHeight"
          stripe
          v-loading="loadingVisible"
          :empty-text="$t('products.empty_results')">
          <el-table-column prop="ArticleNumber" :label="$t('products.article_number')" width="165" show-overflow-tooltip></el-table-column>
          <el-table-column :label="$t('products.name')" min-width="360" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="product-cell">
                <div class="primary-cell">
                  <span>{{ scope.row.Name }}</span>
                  <el-tag v-if="is_pending(scope.row)" size="mini" type="warning">{{ $t("products.pending") }}</el-tag>
                </div>
                <span v-if="scope.row.CategoryName" class="muted-line">{{ scope.row.CategoryName }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="CategoryName" :label="$t('products.category')" width="185" show-overflow-tooltip></el-table-column>
          <el-table-column :label="$t('products.marketplace_price')" width="135" align="right" header-align="right">
            <template slot-scope="scope">
              <span v-if="!is_missing_price(scope.row)" class="number-cell">{{ scope.row.MarketplacePrice|format_money }} &euro;</span>
              <el-tag v-else size="mini" type="danger">{{ $t("products.missing_price") }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('products.catalog')" width="230" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="catalog-cell" v-if="scope.row.CatalogStatus">
                <el-tag size="mini" :type="catalog_status_type(scope.row.CatalogStatus)">{{ scope.row.CatalogStatus }}</el-tag>
                <span>{{ scope.row.CatalogArticleGroup || scope.row.CatalogProductArea || "-" }}</span>
              </div>
              <span v-else class="muted-line">{{ $t("products.manual_or_legacy") }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('products.proposed_by_user')" min-width="190" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.ProposedByUser || "-" }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('products.status')" width="135">
            <template slot-scope="scope">
              <el-tag v-if="is_pending(scope.row)" size="mini" type="warning">{{ $t("products.pending") }}</el-tag>
              <el-tag v-else size="mini" type="success">{{ $t("products.approved") }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column fixed="right" :label="$t('operations')" width="210" align="right" header-align="right">
            <template slot-scope="scope">
              <div class="operation-actions">
                <el-button v-if="is_pending(scope.row)" @click="approve_item(scope.row)" type="text" size="mini" class="approve-action">{{ $t("products.approve") }}</el-button>
                <el-button @click="edit_item(scope.row)" type="text" size="mini">{{ $t("edit") }}</el-button>
                <el-button @click="delete_item(scope.row)" type="text" size="mini" class="danger-action">{{ $t("delete") }}</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="admin-pagination">
          <el-pagination
            background
            @size-change="refresh_info"
            @current-change="refresh_info"
            :page-size.sync="PaginationInfo.PerPage"
            :current-page.sync="PaginationInfo.Page"
            :total="PaginationInfo.RowCount"
            layout="total, prev, pager, next">
          </el-pagination>
        </div>
      </div>
    </div>
    <product-dialog ref="item_dialog" @save="refresh_info" />
  </div>
</template>

<script>
import MainMenu from "@/widgets/MainMenu";
import settings from "@/backend/LocalSettings";
import BasePage from "@/views/BasePage";
import ProductDialog from "@/views/dialogs/ProductDialog";

export default {
  name: "products",
  extends: BasePage,
  components: {
    "mainmenu": MainMenu,
    "product-dialog": ProductDialog
  },
  data () {
      return {
      Categories: [],
      CatalogStatuses: [],
      Results: [],
      Filters: { ArticleCode: "", IdCategory: -1, ApprovalStatus: "", CatalogStatus: "" },
      TableMaxHeight: 620,
      ResizeTimer: null,
      FilterTimer: null,
      RequestSerial: 0,
      PaginationInfo: { Page: 1, PerPage: 18, RowCount: 0, PageSizes: [18] }
    }
  },
  methods: {
    get_default_filters: function() {
      return { ArticleCode: "", IdCategory: -1, ApprovalStatus: "", CatalogStatus: "" };
    },
    async get_info(){
      try {
        var response = await this.post("products/get_info");
        this.Categories = response.Categories;
        this.CatalogStatuses = response.CatalogStatuses || [];
        this.refresh_info();
      } catch(e) {
        this.loadingVisible = false;
        this.$message.error(this.$t("products.load_failed"));
      }
    },
    get_visible_page_size: function() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
      var cardElement = this.$el ? this.$el.querySelector(".admin-card") : null;
      var cardTop = cardElement ? cardElement.getBoundingClientRect().top : 215;
      var paginationHeight = 52;
      var cardHeaderHeight = 64;
      var bottomPadding = 18;
      var availableTableHeight = Math.max(320, viewportHeight - cardTop - paginationHeight - cardHeaderHeight - bottomPadding);
      this.TableMaxHeight = availableTableHeight;

      var rowHeight = 53;
      var tableHeaderHeight = 48;
      var visibleRows = Math.floor((availableTableHeight - tableHeaderHeight) / rowHeight);
      return Math.max(8, Math.min(18, visibleRows));
    },
    fit_page_size_to_viewport: function() {
      var pageSize = this.get_visible_page_size();
      this.PaginationInfo.PageSizes = [pageSize];
      if( this.PaginationInfo.PerPage != pageSize )
      {
        this.PaginationInfo.PerPage = pageSize;
        return true;
      }
      return false;
    },
    on_window_resize: function() {
      clearTimeout(this.ResizeTimer);
      this.ResizeTimer = setTimeout(() => {
        if( this.fit_page_size_to_viewport() )
        {
          this.PaginationInfo.Page = 1;
          this.refresh_info();
        }
      }, 150);
    },
    apply_filters: function() {
      this.PaginationInfo.Page = 1;
      this.refresh_info();
    },
    on_filter_input: function() {
      clearTimeout(this.FilterTimer);
      this.FilterTimer = setTimeout(() => {
        this.apply_filters();
      }, 300);
    },
    async refresh_info() {
      this.fit_page_size_to_viewport();
      var requestSerial = ++this.RequestSerial;
      this.loadingVisible = true;
      this.$http
        .post("products/refresh_info", { Filters: this.Filters, PaginationInfo: this.PaginationInfo })
        .then((response) => {
          if(requestSerial !== this.RequestSerial) return;
          this.loadingVisible = false;
          if( settings.verify_response(response, this.$rights)) {
            this.Results = response.body.Results;
            this.PaginationInfo = response.body.PaginationInfo;
          } else {
            this.$router.push("/");
          }
        })
        .catch(() => {
          if(requestSerial !== this.RequestSerial) return;
          this.loadingVisible = false;
          this.$message.error(this.$t("products.load_failed"));
        });
    },
    add_item: function(){
      this.$refs.item_dialog.show_me();
    },
    edit_item: function(item){
      this.$refs.item_dialog.show_me(item);
    },
    is_missing_price: function(item) {
      return !item || item.MarketplacePrice === null || item.MarketplacePrice === undefined || item.MarketplacePrice === "";
    },
    async approve_item(item) {
      try {
        var confirmed = await this.$confirm(this.$t("products.confirm_approve"), this.$t("warning_title"), {
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          type: "warning"
        });
        if( confirmed )
        {
          await this.post("products/approve_item", { object: item });
          this.refresh_info();
        }
      } catch(e) {}
    },
    async delete_item(item){
      try {
        var confirmed = await this.$confirm(this.$t("confirm_delete"), this.$t("warning_title"), {
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          type: "warning"
        });
        if( confirmed )
        {
          await this.post("products/delete_item", { object: item });
          this.refresh_info();
        }
      } catch(e) {}
    },
    async queryCodes(queryString, cb){
      if(!queryString || queryString.length < 2) {
        cb([]);
        return;
      }
      try {
        var response = await this.$http.post("products/search_code", { query: queryString });
        if( settings.verify_response(response, this.$rights)) {
          cb(response.body.Results);
        } else {
          cb([]);
        }
      } catch(e) {
        cb([]);
      }
    },
    reset_filters: function(){
      clearTimeout(this.FilterTimer);
      this.Filters = this.get_default_filters();
      this.apply_filters();
    },
    is_pending: function(item) {
      return String(item.Approved) === "0";
    },
    catalog_status_type: function(status) {
      if(status === "aktiv") return "success";
      if(status === "auslaufend") return "warning";
      return "info";
    }
  },
  mounted: function() {
    this.fit_page_size_to_viewport();
    window.addEventListener("resize", this.on_window_resize);
    this.get_info();
  },
  beforeDestroy: function() {
    window.removeEventListener("resize", this.on_window_resize);
    clearTimeout(this.ResizeTimer);
    clearTimeout(this.FilterTimer);
  }
};
</script>

<style lang="less" scoped>
@import "../styles/admin-list.less";

.products-filters {
  grid-template-columns: auto minmax(260px, 1.2fr) minmax(210px, 0.7fr) minmax(180px, 0.55fr) minmax(180px, 0.55fr) auto auto;
}

.product-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.catalog-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
</style>
